import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Polyline, CircleMarker, Popup, useMap } from 'react-leaflet';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Play, Pause, ChevronLeft, SkipForward, X } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ROUTE_COLORS = [
  '#ef4444', '#3b82f6', '#22c55e', '#f59e0b', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#a855f7',
  '#10b981', '#e11d48', '#6366f1', '#84cc16', '#0ea5e9',
];

function getRouteColor(routeId, explicitColor) {
  if (explicitColor) {
    const c = explicitColor.replace('#', '').toUpperCase();
    if (c !== 'FFFFFF' && c !== 'FFF' && c !== '000000' && c !== '000') {
      return `#${c}`;
    }
  }
  let hash = 0;
  for (let i = 0; i < routeId.length; i++) {
    hash = routeId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return ROUTE_COLORS[Math.abs(hash) % ROUTE_COLORS.length];
}

function parseGtfsTime(timeStr) {
  if (!timeStr) return null;
  const parts = timeStr.split(':');
  if (parts.length < 3) return null;
  return parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
}

function formatTime(seconds) {
  const h = Math.floor(seconds / 3600) % 24;
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function interpolateOnShape(shapeCoords, progress) {
  if (!shapeCoords || shapeCoords.length < 2) return null;
  if (progress <= 0) return shapeCoords[0];
  if (progress >= 1) return shapeCoords[shapeCoords.length - 1];

  let totalDist = 0;
  const segDists = [];
  for (let i = 1; i < shapeCoords.length; i++) {
    const d = Math.sqrt(
      Math.pow(shapeCoords[i][0] - shapeCoords[i-1][0], 2) +
      Math.pow(shapeCoords[i][1] - shapeCoords[i-1][1], 2)
    );
    segDists.push(d);
    totalDist += d;
  }

  const targetDist = progress * totalDist;
  let accum = 0;
  for (let i = 0; i < segDists.length; i++) {
    if (accum + segDists[i] >= targetDist) {
      const frac = segDists[i] > 0 ? (targetDist - accum) / segDists[i] : 0;
      return [
        shapeCoords[i][0] + frac * (shapeCoords[i+1][0] - shapeCoords[i][0]),
        shapeCoords[i][1] + frac * (shapeCoords[i+1][1] - shapeCoords[i][1]),
      ];
    }
    accum += segDists[i];
  }
  return shapeCoords[shapeCoords.length - 1];
}

function calculateBusPositions(trips, shapes, routes, currentTime) {
  const buses = [];
  for (const trip of trips) {
    const stops = trip.stops;
    if (stops.length < 2) continue;

    const firstArrival = parseGtfsTime(stops[0].arrival || stops[0].departure);
    const lastArrival = parseGtfsTime(stops[stops.length - 1].arrival || stops[stops.length - 1].departure);
    if (firstArrival === null || lastArrival === null) continue;
    if (currentTime < firstArrival || currentTime > lastArrival) continue;

    let prevStop = null, nextStop = null, prevTime = null, nextTime = null;

    for (let i = 0; i < stops.length; i++) {
      const arrTime = parseGtfsTime(stops[i].arrival || stops[i].departure);
      const depTime = parseGtfsTime(stops[i].departure || stops[i].arrival);
      if (arrTime === null) continue;
      if (arrTime <= currentTime) {
        prevStop = stops[i];
        prevTime = depTime || arrTime;
      }
      if (arrTime >= currentTime && !nextStop) {
        nextStop = stops[i];
        nextTime = arrTime;
        break;
      }
    }

    if (!prevStop) continue;

    let lat, lon;
    if (!nextStop || prevTime === nextTime) {
      lat = prevStop.lat;
      lon = prevStop.lon;
    } else {
      const segProgress = Math.max(0, Math.min(1, (currentTime - prevTime) / (nextTime - prevTime)));
      const shapeCoords = shapes[trip.shape_id];
      if (shapeCoords && shapeCoords.length > 2 && lastArrival > firstArrival) {
        const overallProgress = (currentTime - firstArrival) / (lastArrival - firstArrival);
        const pos = interpolateOnShape(shapeCoords, Math.max(0, Math.min(1, overallProgress)));
        if (pos) { lat = pos[0]; lon = pos[1]; }
      }
      if (lat === undefined) {
        lat = prevStop.lat + segProgress * (nextStop.lat - prevStop.lat);
        lon = prevStop.lon + segProgress * (nextStop.lon - prevStop.lon);
      }
    }

    const route = routes[trip.route_id] || {};
    buses.push({
      tripId: trip.trip_id,
      routeId: trip.route_id,
      routeName: route.short_name || route.long_name || trip.route_id,
      routeLongName: route.long_name || '',
      headsign: trip.headsign || '',
      direction: trip.direction,
      color: getRouteColor(trip.route_id, route.color),
      lat, lon,
      prevStopName: prevStop?.name || '',
      nextStopName: nextStop?.name || prevStop?.name || '',
    });
  }
  return buses;
}

function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds?.length > 0) map.fitBounds(bounds, { padding: [30, 30] });
  }, [map, bounds]);
  return null;
}

const VisualizerPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [displayTime, setDisplayTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(10);
  const [buses, setBuses] = useState([]);
  const [selectedRouteId, setSelectedRouteId] = useState(null);

  // Refs for smooth animation
  const timeRef = useRef(0);
  const lastFrameRef = useRef(null);
  const rafRef = useRef(null);
  const speedRef = useRef(speed);
  const playingRef = useRef(playing);
  const dataRef = useRef(null);

  // Keep refs in sync
  useEffect(() => { speedRef.current = speed; }, [speed]);
  useEffect(() => { playingRef.current = playing; }, [playing]);
  useEffect(() => { dataRef.current = data; }, [data]);

  // Initialize time
  useEffect(() => {
    const now = new Date();
    const t = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
    timeRef.current = t;
    setDisplayTime(t);
  }, []);

  // Load data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API_URL}/projects/${projectId}/visualizer-data`);
        setData(res.data);
      } catch (error) {
        toast.error('Error al cargar datos del visualizador');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [projectId]);

  // Smooth animation loop with requestAnimationFrame
  useEffect(() => {
    const animate = (timestamp) => {
      if (!playingRef.current || !dataRef.current) {
        lastFrameRef.current = null;
        rafRef.current = requestAnimationFrame(animate);
        return;
      }

      if (lastFrameRef.current !== null) {
        const deltaMs = timestamp - lastFrameRef.current;
        const deltaSec = (deltaMs / 1000) * speedRef.current;
        timeRef.current += deltaSec;
        if (timeRef.current >= 86400) timeRef.current -= 86400;
        if (timeRef.current < 0) timeRef.current += 86400;

        // Update display and bus positions
        const t = timeRef.current;
        setDisplayTime(t);
        const positions = calculateBusPositions(
          dataRef.current.trips, dataRef.current.shapes, dataRef.current.routes, t
        );
        setBuses(positions);
      }

      lastFrameRef.current = timestamp;
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Initial bus calculation when data loads
  useEffect(() => {
    if (data && !playing) {
      const positions = calculateBusPositions(data.trips, data.shapes, data.routes, timeRef.current);
      setBuses(positions);
    }
  }, [data, playing]);

  const handleSliderChange = useCallback((e) => {
    const t = parseInt(e.target.value);
    timeRef.current = t;
    setDisplayTime(t);
    if (dataRef.current) {
      const positions = calculateBusPositions(
        dataRef.current.trips, dataRef.current.shapes, dataRef.current.routes, t
      );
      setBuses(positions);
    }
  }, []);

  const handleRouteClick = useCallback((routeId) => {
    setSelectedRouteId(prev => prev === routeId ? null : routeId);
  }, []);

  const speeds = [1, 5, 10, 30, 60, 120];

  const mapBounds = useMemo(() => {
    if (!data?.trips) return null;
    const lats = [], lons = [];
    data.trips.forEach(t => t.stops.forEach(s => {
      if (s.lat && s.lon) { lats.push(s.lat); lons.push(s.lon); }
    }));
    if (lats.length === 0) return null;
    return [[Math.min(...lats), Math.min(...lons)], [Math.max(...lats), Math.max(...lons)]];
  }, [data]);

  // Route polylines with route_id mapping
  const routePolylines = useMemo(() => {
    if (!data?.shapes || !data?.routes || !data?.trips) return [];
    const shapeRouteMap = {};
    data.trips.forEach(t => {
      if (t.shape_id && !shapeRouteMap[t.shape_id]) shapeRouteMap[t.shape_id] = t.route_id;
    });
    return Object.entries(data.shapes).map(([shapeId, coords]) => {
      const routeId = shapeRouteMap[shapeId];
      const route = data.routes[routeId] || {};
      return {
        shapeId, coords, routeId,
        color: getRouteColor(routeId || shapeId, route.color),
        routeName: route.short_name || shapeId,
        routeLongName: route.long_name || '',
      };
    });
  }, [data]);

  // Stops for the selected route (unique stops with sequence)
  const selectedRouteStops = useMemo(() => {
    if (!selectedRouteId || !data?.trips) return [];
    const stopMap = {};
    data.trips
      .filter(t => t.route_id === selectedRouteId)
      .forEach(t => {
        t.stops.forEach(s => {
          if (!stopMap[s.stop_id]) {
            stopMap[s.stop_id] = { ...s };
          }
        });
      });
    return Object.values(stopMap);
  }, [selectedRouteId, data]);

  // Selected route info
  const selectedRouteInfo = useMemo(() => {
    if (!selectedRouteId || !data?.routes) return null;
    const r = data.routes[selectedRouteId];
    if (!r) return null;
    return {
      ...r,
      color: getRouteColor(selectedRouteId, r.color),
      activeBuses: buses.filter(b => b.routeId === selectedRouteId).length,
      totalStops: selectedRouteStops.length,
    };
  }, [selectedRouteId, data, buses, selectedRouteStops]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-900">
        <div className="text-lg text-slate-300">Cargando datos del visualizador...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-900">
        <Card className="p-8 text-center bg-slate-800 border-slate-700">
          <p className="text-slate-300">No se pudieron cargar los datos</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>Volver</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-slate-900">
      {/* Map */}
      <div className="flex-1 relative">
        <MapContainer
          center={[43.3, -2.0]}
          zoom={12}
          className="h-full w-full"
          style={{ background: '#1e293b' }}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; <a href="https://carto.com">CARTO</a>'
          />
          {mapBounds && <FitBounds bounds={mapBounds} />}

          {/* Route polylines */}
          {routePolylines.map(rp => {
            const isSelected = selectedRouteId === rp.routeId;
            const isDimmed = selectedRouteId && !isSelected;
            return (
              <Polyline
                key={rp.shapeId}
                positions={rp.coords}
                pathOptions={{
                  color: rp.color,
                  weight: isSelected ? 5 : 3,
                  opacity: isDimmed ? 0.15 : isSelected ? 1 : 0.6,
                }}
                eventHandlers={{
                  click: () => handleRouteClick(rp.routeId),
                }}
              />
            );
          })}

          {/* Selected route stops */}
          {selectedRouteStops.map(stop => (
            <CircleMarker
              key={`stop-${stop.stop_id}`}
              center={[stop.lat, stop.lon]}
              radius={4}
              pathOptions={{
                color: selectedRouteInfo?.color || '#fff',
                weight: 2,
                fillColor: '#fff',
                fillOpacity: 1,
              }}
            >
              <Popup>
                <p className="font-bold text-xs">{stop.name}</p>
                <p className="text-[10px] text-slate-500">ID: {stop.stop_id}</p>
              </Popup>
            </CircleMarker>
          ))}

          {/* Bus markers */}
          {buses.map(bus => {
            const isDimmed = selectedRouteId && bus.routeId !== selectedRouteId;
            return (
              <CircleMarker
                key={bus.tripId}
                center={[bus.lat, bus.lon]}
                radius={isDimmed ? 4 : 7}
                pathOptions={{
                  color: bus.color,
                  weight: 2,
                  fillColor: bus.color,
                  fillOpacity: isDimmed ? 0.3 : 1,
                  opacity: isDimmed ? 0.3 : 1,
                }}
              >
                <Popup>
                  <div className="min-w-48">
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                        style={{ backgroundColor: bus.color }}
                      >
                        {bus.routeName.substring(0, 3)}
                      </div>
                      <div>
                        <p className="font-bold text-sm">{bus.routeName}</p>
                        <p className="text-xs text-slate-500">{bus.routeLongName}</p>
                      </div>
                    </div>
                    {bus.headsign && (
                      <p className="text-xs mb-1">Destino: <strong>{bus.headsign}</strong></p>
                    )}
                    <p className="text-xs">Dir: {bus.direction === '0' ? 'Ida' : 'Vuelta'}</p>
                    <hr className="my-1" />
                    <p className="text-xs">Anterior: <strong>{bus.prevStopName}</strong></p>
                    <p className="text-xs">Siguiente: <strong>{bus.nextStopName}</strong></p>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>

        {/* Back button */}
        <div className="absolute top-4 left-4 z-[1000]">
          <Button
            data-testid="back-from-visualizer"
            size="sm"
            variant="secondary"
            className="bg-slate-800/90 text-white hover:bg-slate-700 border-slate-600"
            onClick={() => navigate(`/projects/${projectId}/editor`)}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Volver
          </Button>
        </div>

        {/* Bus count + selected route info */}
        <div className="absolute top-4 right-4 z-[1000] flex flex-col items-end gap-2">
          <Badge className="bg-slate-800/90 text-white border-slate-600 text-sm px-3 py-1">
            {buses.length} buses activos
          </Badge>

          {selectedRouteInfo && (
            <Card className="bg-slate-800/95 border-slate-600 p-3 min-w-56" data-testid="selected-route-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: selectedRouteInfo.color }} />
                  <span className="text-white font-bold text-sm">{selectedRouteInfo.short_name}</span>
                </div>
                <Button
                  data-testid="deselect-route-btn"
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6 text-slate-400 hover:text-white"
                  onClick={() => setSelectedRouteId(null)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
              <p className="text-xs text-slate-300 mb-1">{selectedRouteInfo.long_name}</p>
              <div className="flex gap-3 text-xs text-slate-400">
                <span>{selectedRouteInfo.activeBuses} buses</span>
                <span>{selectedRouteInfo.totalStops} paradas</span>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Controls bar */}
      <div className="bg-slate-800 border-t border-slate-700 px-6 py-4">
        <div className="flex items-center gap-4">
          <Button
            data-testid="play-pause-btn"
            size="icon"
            variant="ghost"
            className="text-white hover:bg-slate-700 h-10 w-10"
            onClick={() => {
              setPlaying(!playing);
              lastFrameRef.current = null; // Reset frame timing
            }}
          >
            {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          </Button>

          <div
            className="font-mono text-2xl font-bold text-white min-w-28 text-center"
            data-testid="current-time-display"
          >
            {formatTime(displayTime)}
          </div>

          <div className="flex-1 mx-4">
            <input
              data-testid="time-slider"
              type="range"
              min="0"
              max="86399"
              value={Math.floor(displayTime)}
              onChange={handleSliderChange}
              className="w-full h-2 bg-slate-600 rounded-lg appearance-none cursor-pointer accent-blue-500"
            />
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>00:00</span>
              <span>06:00</span>
              <span>12:00</span>
              <span>18:00</span>
              <span>23:59</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <SkipForward className="w-4 h-4 text-slate-400" />
            <div className="flex gap-1">
              {speeds.map(s => (
                <Button
                  key={s}
                  data-testid={`speed-${s}x`}
                  size="sm"
                  variant={speed === s ? 'default' : 'ghost'}
                  className={speed === s
                    ? 'bg-blue-600 hover:bg-blue-700 text-white h-8 px-2 text-xs'
                    : 'text-slate-400 hover:bg-slate-700 hover:text-white h-8 px-2 text-xs'
                  }
                  onClick={() => setSpeed(s)}
                >
                  {s}x
                </Button>
              ))}
            </div>
          </div>

          <div className="text-xs text-slate-400 text-right min-w-20">
            <p>{data.trip_count} viajes</p>
            <p>{data.stop_count} paradas</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisualizerPage;
